import {
  ToastContainerDS,
  LaunchPage,
  sortByDateTime,
  PageDS,
  Tag,
  TableTagVehicleStatus,
  TableTagVehicleConnectionStatus,
  Badge,
  MessageTooltip,
  TextCapitalized,
  MaterialUiTable,
  usePersistMuiTableState,
  renderCellWithLabel,
  MaterialUiColumnsProps,
  dateFormatterDayMonthYear,
} from "@qivia/ui";
import { Trans, useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  selectListAllVehiclesInDb,
  selectListAllVehiclesInDbStatus,
  selectVehiclesDateRange,
  // selectVehiclesEndDatePeriod,
} from "./vehiclesSlice";
import { VehicleStatus } from "@qivia/schemas-cards-vehicles";
import { VehiclesListType, VehiclesListDisplayedType } from "./vehiclesAPI";
import type {
  VehicleConnectionStatus,
  TelemetryDataProvider,
} from "@qivia/schemas-cards-vehicles";
import styled from "styled-components";
import Car from "@qivia/ui/src/designSystem/assets/Car.png";
import { Spacer } from "@qivia/ui/src/designSystem/components/Spacer";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { documentDownloadXlsxAsync } from "../../../libSlice/downloadFileXslx";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import {
  formatCurrency,
  formatMileage,
  formatCarbonEmissionPerKilometer,
  formatLiterPerOneHundredMileage,
  formatDateTime,
} from "../../../libSlice/materialUiFormats";
import "../../../i18n/i18n";
import { useSearchParams } from "react-router-dom";
import { useRef } from "react";
import { selectDepartments } from "../departments/departmentsSlice";
import { ErrorPage } from "../../../components/ErrorPage";

interface SharedInterfaceVehiclesProps {
  hasAdvancedAnalytics?: boolean;
  sidePanelAction?: (props: Partial<VehiclesListType>) => void;
  sidePanel?: JSX.Element;
  otherSupportPhones?: {
    name: string;
    phone: string;
  }[];
  cta?: {
    title: string;
    action: () => void;
    leftIcon?: JSX.Element | null;
  };
  cta2?: {
    title: string;
    action: () => void;
    leftIcon?: JSX.Element | null;
  };
  cta3?: {
    title: string;
    action: () => void;
    leftIcon?: JSX.Element | null;
  };
  reloadListWithDateFilters?: (dates: Date[]) => void;
  reloadListWithEndDatePeriod?: (date: Date) => void;
}

export const SharedInterfaceVehicles = (
  props: SharedInterfaceVehiclesProps,
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const location = useLocation();
  const departments = useAppSelector(selectDepartments);

  const allVehiclesInDb = useAppSelector(selectListAllVehiclesInDb);
  const listAllVehiclesInDbStatus = useAppSelector(
    selectListAllVehiclesInDbStatus,
  );
  const vehiclesDateRange = useAppSelector(selectVehiclesDateRange);
  // const vehiclesEndDatePeriod = useAppSelector(selectVehiclesEndDatePeriod);
  const [listDisplayed, setListDisplayed] = useState<
    VehiclesListDisplayedType[] | null
  >(null);

  const [messageTooltip, setMessageTooltip] = useState<JSX.Element | null>(
    null,
  );
  const [titleTooltip, setTitleTooltip] = useState<string>("");
  const [rowToSendToTop, setRowToSendToTop] = useState<string | null>(null);

  const pageName = "vehicles";
  const [searchParams] = useSearchParams();
  const fromNavigate = searchParams.get("fromNavigate") === "true";

  const hasExecutedFromNavigate = useRef(false);
  const shouldExecuteFromNavigate = useRef(false);

  if (fromNavigate && !shouldExecuteFromNavigate.current) {
    shouldExecuteFromNavigate.current = true;
  }

  const findKeysToTranslate = useCallback(
    (key: keyof VehiclesListType, value: string) => {
      if (key === "status") {
        return t(`vehicles.status.${value}`);
      } else if (key === "connectionStatus") {
        return value
          ? t(`vehicles.connectionStatus.${value}`)
          : t("vehicles.connectionStatus.error");
      } else if (key === "dataProvider") {
        return t(`vehicles.dataProvider.${value}`);
      } else return value;
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () =>
      ["status", "connectionStatus", "dataProvider"] as (
        | keyof VehiclesListType
        | keyof VehiclesListDisplayedType
      )[],
    [],
  );

  const apiRef = useGridApiRef();
  const key = "customers-grid";
  const aggregation = {
    model: {
      reference: "size",
      registrationNumber: "size",
      brand: "size",
      status: "size",
      lastDriver: "size",
      latestMileage: "avg",
      averagePricePerLiter: "avg",
      averageConsumption: "avg",
      averageCarbonEmissionPerKm: "avg",
      analyticCode: "size",
      mileageOfAPeriod: "avg",
    },
  };
  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const listAllVehiclesInDb: (VehiclesListType & {
    id: string;
    departmentName: string | null;
  })[] = useMemo(() => {
    const rowsWithId = allVehiclesInDb.map((av) => {
      const departmentName = departments.find(
        (department) => department.uuid === av.department,
      )?.name;
      let newDict: VehiclesListType & {
        id: string;
        departmentName: string | null;
      } = {
        ...av,
        id: av.uuid,
        departmentName: departmentName ?? null,
      };
      Object.entries(av).forEach(([key, value]) => {
        if (keysToTranslate.includes(key as keyof VehiclesListDisplayedType)) {
          newDict = {
            ...newDict,
            [key]:
              value || key === "connectionStatus"
                ? findKeysToTranslate(
                    key as keyof VehiclesListType,
                    value as string,
                  )
                : "",
          };
        }
      });
      return newDict;
    });
    return sortByDateTime(rowsWithId, "desc");
  }, [allVehiclesInDb, findKeysToTranslate, keysToTranslate, departments]);

  useEffect(() => {
    if (params.id) {
      const vehicleSelected = listAllVehiclesInDb.find(
        (v) => v.uuid === params.id,
      );
      if (vehicleSelected) {
        const firstPath = location.pathname.split("/")[1]; // home or departments
        navigate(`/${firstPath}/vehicles/${vehicleSelected.uuid}`);
      }
    }
  }, [listAllVehiclesInDb, location.pathname, navigate, params.id]);

  useEffect(() => {
    if (listAllVehiclesInDb && listAllVehiclesInDbStatus === "success") {
      const newVehiclesList = listAllVehiclesInDb.filter((v) => v.isNewVehicle);
      const updatedVehiclesList = listAllVehiclesInDb.filter(
        (v) => v.isUpdatedVehicle,
      );
      const alreadyExistedVehiclesList = listAllVehiclesInDb.filter(
        (v) => v.isAlreadyExisted,
      );

      const stringifyAlreadyVehiclesList = alreadyExistedVehiclesList
        .map((vehicle) => {
          return [vehicle.registrationNumber, vehicle.reference]
            .filter((d) => d)
            .join(" / ");
        })
        .join(", ");

      const vehiclesImportLength =
        newVehiclesList.length +
        updatedVehiclesList.length +
        alreadyExistedVehiclesList.length;

      const toolTipMessageAllVehicles =
        vehiclesImportLength > 0
          ? vehiclesImportLength === 1
            ? t("vehicles.status.messageTooltip.importAll.singular")
            : t("vehicles.status.messageTooltip.importAll.plural", {
                number: vehiclesImportLength.toString(),
              })
          : "";

      setTitleTooltip(toolTipMessageAllVehicles);

      const toolTipMessageCreated =
        newVehiclesList.length > 0
          ? newVehiclesList.length === 1
            ? t("vehicles.status.messageTooltip.creation.singular")
            : t("vehicles.status.messageTooltip.creation.plural", {
                number: newVehiclesList.length.toString(),
              })
          : "";

      const toolTipMessageUpdated =
        updatedVehiclesList.length > 0
          ? updatedVehiclesList.length === 1
            ? t("vehicles.status.messageTooltip.update.singular")
            : t("vehicles.status.messageTooltip.update.plural", {
                number: updatedVehiclesList.length.toString(),
              })
          : "";

      const toolTipMessageAlreadyExists =
        alreadyExistedVehiclesList.length > 0 ? (
          alreadyExistedVehiclesList.length === 1 ? (
            <Trans
              i18nKey={t(
                "vehicles.status.messageTooltip.alreadyExists.singular",
                {
                  registrationNumbers: stringifyAlreadyVehiclesList,
                },
              )}
            />
          ) : (
            <Trans
              i18nKey={t(
                "vehicles.status.messageTooltip.alreadyExists.plural",
                {
                  number: alreadyExistedVehiclesList.length.toString(),
                  registrationNumbers: stringifyAlreadyVehiclesList,
                },
              )}
            />
          )
        ) : (
          ""
        );

      setMessageTooltip(
        vehiclesImportLength > 0 ? (
          <StyledColumn>
            <div>
              <TextCapitalized>{toolTipMessageCreated}</TextCapitalized>
            </div>
            {toolTipMessageCreated && toolTipMessageUpdated && (
              <Spacer y={0.5} />
            )}
            <div>
              <TextCapitalized>{toolTipMessageUpdated}</TextCapitalized>
            </div>
            {toolTipMessageUpdated && toolTipMessageAlreadyExists && (
              <Spacer y={0.5} />
            )}
            <div>
              <TextCapitalized>{toolTipMessageAlreadyExists}</TextCapitalized>
            </div>
          </StyledColumn>
        ) : null,
      );

      const vehiclesDateTimeSorted = sortByDateTime(
        listAllVehiclesInDb,
        "desc",
      );

      setListDisplayed(vehiclesDateTimeSorted);
    }
  }, [listAllVehiclesInDbStatus, listAllVehiclesInDb, t]);

  // Only after a navigate from an other page like transaction, we select the row to send to the top of the MaterialUiTable
  useEffect(() => {
    if (
      params.id &&
      listDisplayed &&
      listAllVehiclesInDb &&
      shouldExecuteFromNavigate.current &&
      !hasExecutedFromNavigate.current
    ) {
      hasExecutedFromNavigate.current = true;
      shouldExecuteFromNavigate.current = false;

      const vehicleInDb = listAllVehiclesInDb.find(
        (vehicle) => vehicle.uuid === params.id,
      );

      if (vehicleInDb) {
        apiRef.current.setRowSelectionModel([vehicleInDb.uuid]);
        setRowToSendToTop(vehicleInDb.uuid);
      }
    }
  }, [params.id, listDisplayed, listAllVehiclesInDb, apiRef]);

  useEffect(() => {
    if (rowToSendToTop) {
      setRowToSendToTop(null);
    }
  }, [rowToSendToTop]);

  const searchBarProps = {
    values: listAllVehiclesInDb as (VehiclesListType & {
      id: string;
      departmentName: string | null;
    })[],
    setFilterValues: setListDisplayed,
    keysToTranslate: ["lastTelemetryDataDate" as const],
    findKeysToTranslate: (
      _key: keyof VehiclesListDisplayedType,
      value: string,
    ) => (value ? formatDateTime(value) : ""),
    keysToIgnore: [
      "date" as const,
      "uuid" as const,
      "latestMileage" as const,
      "latestMileageDate" as const,
      "rule" as const,
      "ruleName" as const,
      "cardUuid" as const,
      "cardName" as const,
      "energy" as const,
      "vehicleType" as const,
      "deliveryDate" as const,
      "createdAt" as const,
      "isNewVehicle" as const,
      "isAlreadyExisted" as const,
      "isUpdatedVehicle" as const,
      "updatedAt" as const,
      "driverUuid" as const,
      "driverPhone" as const,
      "energyClass" as const,
      "critAir" as const,
      "lastLatitude" as const,
      "lastLongitude" as const,
      "lastPositionDate" as const,
      "deviceLastActivityDate" as const,
      "dateMec" as const,
      "requestedConnectionAt" as const,
      "isEligible" as const,
      ...(!props.hasAdvancedAnalytics
        ? [
            "connectionStatus" as const,
            "dataProvider" as const,
            "lastTelemetryDataDate" as const,
          ]
        : []),
    ],
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("vehicles.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  const columnsDisplayed: {
    [index in keyof VehiclesListDisplayedType]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      reference: {
        type: "string",
        width: 250,
        valueFormatter: (value) =>
          value && (
            <StyledRow>
              <div style={{ width: "2rem" }}>
                <img
                  src={Car}
                  style={{ width: "100%", filter: "grayscale(100%)" }}
                  alt="Car"
                />
              </div>
              <Spacer x={0.5} />
              <Tag
                textColor={colors["colors/text/black"]}
                backgroundColor={
                  colors["colors/surfaces/background/background_level0"]
                }
                text={value}
                borderColor={colors["colors/borders/cells/cells"]}
              />
            </StyledRow>
          ),
      },
      registrationNumber: {
        type: "string",
        width: 250,
        renderCell: (p) => {
          const isNewVehicle = p.row["isNewVehicle"] as string;
          const isUpdatedVehicle = p.row["isUpdatedVehicle"] as string;
          return renderCellWithLabel(
            p,
            "Éléments",
            <>
              {isNewVehicle || isUpdatedVehicle ? (
                <StyledRow>
                  {p.formattedValue as string}
                  <Spacer x={0.5} />
                  <Badge
                    text={t(
                      `vehicles.status.badge.${isNewVehicle ? "new" : "updated"}`,
                    )}
                    backgroundColor={
                      colors["colors/system/informative/informative_ultraLight"]
                    }
                    textColor={
                      colors["colors/system/informative/informative_normal"]
                    }
                  />
                </StyledRow>
              ) : (
                ((p.formattedValue as string) ?? "")
              )}
            </>,
          );
        },
      },
      brandAndModel: {
        type: "string",
        width: 300,
        valueFormatter: (value: string) =>
          value && (
            <StyledRow>
              <StyledBrand>{value}</StyledBrand>
            </StyledRow>
          ),
      },
      status: {
        type: "string",
        valueFormatter: (value: VehicleStatus) =>
          value && (
            <StyledRow>
              <TableTagVehicleStatus status={value as VehicleStatus} />
            </StyledRow>
          ),
      },
      ...(props.hasAdvancedAnalytics
        ? {
            connectionStatus: {
              type: "string",
              valueFormatter: (value: VehicleConnectionStatus) =>
                value &&
                (value as string) !== t("vehicles.connectionStatus.error") ? (
                  <StyledRow>
                    <TableTagVehicleConnectionStatus
                      connectionStatus={value as VehicleConnectionStatus}
                    />
                  </StyledRow>
                ) : (
                  <StyledRow>
                    <Tag
                      text={t("vehicles.connectionStatus.error")}
                      backgroundColor={
                        colors["colors/system/error/error_ultraLight"]
                      }
                      textColor={colors["colors/system/error/error_normal"]}
                    />
                  </StyledRow>
                ),
            },
          }
        : {}),
      ...(props.hasAdvancedAnalytics
        ? {
            dataProvider: {
              type: "string",
              valueFormatter: (value: TelemetryDataProvider) =>
                value ? (
                  <StyledRow>
                    <TextCapitalized>{value}</TextCapitalized>
                  </StyledRow>
                ) : (
                  "-"
                ),
            },
          }
        : {}),
      dateMec: {
        type: "string",
        width: 250,
        valueFormatter: (value) =>
          value ? dateFormatterDayMonthYear(new Date(value)) : "",
      },
      departmentName: {
        type: "string",
        width: 250,
      },
      lastDriver: {
        type: "string",
        width: 250,
      },
      latestMileage: {
        type: "number",
        valueFormatter: formatMileage,
      },
      ...(props.hasAdvancedAnalytics
        ? {
            mileageOfAPeriod: {
              type: "number",
              valueFormatter: formatMileage,
            },
          }
        : {}),
      ...(props.hasAdvancedAnalytics
        ? {
            lastTelemetryDataDate: {
              type: "string",
              valueFormatter: formatDateTime,
            },
          }
        : {}),
      analyticCode: {
        type: "string",
      },
      averageConsumption: {
        type: "number",
        width: 200,
        valueFormatter: formatLiterPerOneHundredMileage,
      },
      averageCarbonEmissionPerKm: {
        type: "number",
        width: 250,
        valueFormatter: formatCarbonEmissionPerKilometer,
      },
      averagePricePerLiter: {
        type: "number",
        valueFormatter: formatCurrency,
      },
    };
  }, [props.hasAdvancedAnalytics, t]);

  if (listAllVehiclesInDbStatus === "failed") return <ErrorPage />;
  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <>
      <PageDS
        title={t("vehicles.title")}
        toaster={<ToastContainerDS />}
        topElement={
          messageTooltip ? (
            <MessageTooltip
              title={titleTooltip}
              text={messageTooltip}
              type={"INFORMATIVE"}
            />
          ) : (
            ""
          )
        }
        supportButton={{
          showMail: props.otherSupportPhones ? !props.otherSupportPhones : true,
          otherPhones: props.otherSupportPhones ?? undefined,
        }}
        cta={props.cta}
        cta2={props.cta2}
        cta3={props.cta3}
      >
        <MaterialUiTable<
          keyof VehiclesListDisplayedType,
          VehiclesListDisplayedType
        >
          rows={listDisplayed}
          columns={columnsDisplayed}
          pageName={pageName}
          onRowClickAction={props.sidePanelAction}
          dataGridState={dataGridState}
          searchBar={searchBarProps}
          apiRef={apiRef}
          downloadViewAction={downloadViewAction}
          cta={props.cta}
          cta2={props.cta2}
          rowToSendToTop={rowToSendToTop}
          dateRange={
            props.reloadListWithDateFilters
              ? {
                  dateFiltersCallBack: (dates: Date[]) => {
                    props.reloadListWithDateFilters?.(dates);
                  },
                  dateRangeCallBack: vehiclesDateRange,
                }
              : undefined
          }
          // singleDate={
          //   props.reloadListWithEndDatePeriod
          //     ? {
          //         singleDateFilterCallBack: (date: Date) => {
          //           props.reloadListWithEndDatePeriod?.(date);
          //         },
          //         singleDateCallBack: vehiclesEndDatePeriod
          //           ? new Date(vehiclesEndDatePeriod)
          //           : undefined,
          //         title: t("vehicles.dateEndPeriod"),
          //       }
          //     : undefined
          // }
        />
        {props.sidePanel ? props.sidePanel : <></>}
      </PageDS>
    </>
  );
};

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const StyledBrand = styled.div`
  ${typographies["Body/M"]}
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
`;
